import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

import useAuth from "src/hooks/useAuth";
import useNotification from "src/hooks/notification";
import ScanditScanner from "src/components/barcode/ScanditScanner/ScanditScanner";
import { getScanFields, findItemsByMobileLocation } from "src/utils/assetUtils";
import IWOBulkStagingOrReturnModal from "./IWOBulkStagingOrReturnModal";
import StagingOrReturnModal from "./ItemDetails/StagingOrReturnModal";

export default function IWOMasterScanOutModal({
  iwoItems,
  projectId,
  internalWorkOrder,
  onClose,
  onScanComplete,
}) {
  const { user } = useAuth();
  const { notifyError } = useNotification();
  const [openScanner, setOpenScanner] = useState(true);
  const [bulkItems, setBulkItems] = useState(null);
  const [singleMatch, setSingleMatch] = useState(null);

  const handleScan = scannedBarcode => {
    const activeItems = iwoItems.filter(
      item => item.asset && item.asset.type !== "Kit" && !item.completedStatus
    );

    for (const item of activeItems) {
      const scanFields = getScanFields(item.asset);
      const scanField = scanFields.find(([value]) => value === scannedBarcode);
      if (scanField) {
        setOpenScanner(false);
        setSingleMatch(item);
        return;
      }
    }

    const mobileMatches = findItemsByMobileLocation(activeItems, scannedBarcode);
    if (mobileMatches.length > 0) {
      if (mobileMatches.length === 1) {
        setOpenScanner(false);
        setSingleMatch(mobileMatches[0]);
        return;
      }

      if (mobileMatches.some(item => item.nonAllocating)) {
        notifyError(
          "Non-allocating items matched by location must be processed individually so you can choose the current source location."
        );
        setOpenScanner(false);
        return;
      }

      setOpenScanner(false);
      setBulkItems(mobileMatches);
      return;
    }

    notifyError(
      "No items matched. Please confirm you are scanning an asset identifier or mobile warehouse location for this work order."
    );
    setOpenScanner(false);
  };

  const handleScanAgain = () => {
    setBulkItems(null);
    setSingleMatch(null);
    setOpenScanner(true);
  };

  if (bulkItems) {
    return (
      <IWOBulkStagingOrReturnModal
        items={bulkItems}
        projectId={projectId}
        internalWorkOrder={internalWorkOrder}
        onClose={onClose}
        onSuccess={onScanComplete}
      />
    );
  }

  if (singleMatch) {
    const mode = singleMatch.pickedStatus ? "return" : "staging";
    return (
      <StagingOrReturnModal
        open
        mode={mode}
        asset={singleMatch.asset}
        item={singleMatch}
        internalWorkOrder={internalWorkOrder}
        onClose={onClose}
        onSuccess={onScanComplete}
      />
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      {openScanner ? (
        <>
          <DialogTitle>Scan Out</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" height="100%" justifyContent="space-between">
              <ScanditScanner
                onDetected={handleScan}
                onClose={() => setOpenScanner(false)}
              />
            </Box>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle>
            <Typography variant="h5" align="center" color="black" fontWeight={500}>
              No Match Found
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" alignItems="center" justifyContent="center" minHeight="100px">
              <Typography variant="body1" align="center" color="error" fontWeight={500}>
                The scanned value did not match any item or mobile location on this work order.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleScanAgain} color="primary" variant="contained">
              Scan Again
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
